import { Banner, LegacyCard, Layout, Link, Page, SkeletonBodyText, SkeletonDisplayText, LegacyStack, Text, Button, Icon, MediaCard, List, BlockStack, TextContainer, Card, InlineGrid, InlineStack, IndexTable, Badge, Tooltip, Box, Spinner, Modal, TextField, FormLayout } from '@shopify/polaris';
import React, { useState, useEffect, useCallback } from 'react';
import { SkeletonPageCustom } from './SkeletonPageCustom';
import { RefreshIcon, ExternalSmallIcon, NotificationIcon, ChartVerticalFilledIcon, SearchIcon, DiscountIcon, PlusIcon, ClipboardIcon, StarFilledIcon, StarIcon, XSmallIcon, CheckCircleIcon, XCircleIcon } from '@shopify/polaris-icons';

import { starIconCustom, qsRefreshIconCustom, qsBarIconCustom, qsDiscountIconCustom, qsDollarIconCustom, automaticIconCustom, codeIconCustom, bulkIconCustom } from "../assets/images";
import { formatMoney, copyToClipboard, isValidEmail, defaultInitSettings } from './../Utils';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate, useSafeAppBridge } from "../hooks";

export function Home(props) {
    const shopify = useSafeAppBridge();
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    //const [initState, setInitState] = useState({});
    const [model, setModel] = useState({ enabled: true, liveMode: true });
    const [quickStats, setQuickStats] = useState(null);
    const [recentCampaigns, setRecentCampaigns] = useState(null);
    const [rating, setRating] = useState(null);
    const [modalFeedbackActive, setModalFeedbackActive] = useState(false);
    const [liveModeLoading, setLiveModeLoading] = useState(false);
    const [enabledLoading, setEnabledLoading] = useState(false);
    const [stateLoading, setStateLoading] = useState(false);
    const [syncProductsLoading, setSyncProductsLoading] = useState(false);
    const [syncCustomersLoading, setSyncCustomersLoading] = useState(false);
    const [error, setError] = useState({ feedback: false, email: false });
    const [redirectUrl, setRedirectUrl] = useState('');

    const [initSettings, setInitSettings] = useState(() => {
        const domain = props.user?.myShopifyDomain;

        if (!domain) {
            return defaultInitSettings;
        }

        const stored = localStorage.getItem(`${domain}_initSettings`);

        return JSON.parse(stored || "null") || defaultInitSettings;
    });

    const fetch = FetchCustom();

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/index');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            //setInitState(data);
            setModel(data);
            setLoading(false);
        }
    };

    const getQuickStats = async (dateRange) => {
        setQuickStats(null);
        updateInitSetting('qsDateRange', dateRange);

        const url = dateRange ? `api/home/quickStats?dateRange=${dateRange}` : 'api/home/quickStats';
        const data = await fetch(url);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setQuickStats(data);
        }
    };

    const getRecentCampaigns = async () => {
        const data = await fetch('api/home/recentCampaigns');

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setRecentCampaigns(data);
            updateInitSetting('numberOfCampaigns', data.items.length);
        }
    };

    const getRating = async () => {
        const data = await fetch('api/home/rating');

        if (data?.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setRating(data);
            updateInitSetting('showRating', data !== null);
        }
    };

    const saveRating = async (rate) => {
        const updated = {
            ...rating,
            rate: rate
        };
        setRating(updated);

        if (rate === 5 && shopify) {
            updateInitSetting('showRating', false);
            await shopify.reviews.request();
            await fetch('api/home/saveRating', { rate: rate });
        }
        else {
            setModalFeedbackActive(true);
        }
    };

    const closeRating = async () => {
        updateInitSetting('showRating', false);
        await fetch('api/home/closeRating', {});
    };

    const getMainThemeSettingsData = async () => {
        const data = await fetch('api/home/getMainThemeSettingsData');

        setEnabledLoading(false);
        setStateLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.enabled = !data.coreEmbedBlockDisabled;
                return model;
            });
            updateInitSetting('appEnabled', !data.coreEmbedBlockDisabled);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const updateInitSetting = (keyName, value) => {
        if (!props.user?.myShopifyDomain) return;

        const key = `${props.user.myShopifyDomain}_initSettings`;

        setInitSettings(current => {
            const updated = {
                ...current,
                [keyName]: value,
            };

            localStorage.setItem(key, JSON.stringify(updated));

            return updated;
        });
    };

    const changeLiveMode = async (liveMode) => {
        props.setIsLoading(true);
        setLiveModeLoading(true);

        const data = await fetch('api/home/changeLiveMode', { liveMode: liveMode });

        props.setIsLoading(false);
        setLiveModeLoading(false);

        if (data) {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.liveMode = liveMode;
                return model;
            });

            props.changeToastActive(true, 'The app is in ' + (liveMode ? 'Live' : 'Preview') + ' mode.');
        }
    };

    const syncProducts = async () => {
        setSyncProductsLoading(true);
        props.setIsLoading(true);
        const data = await fetch('api/home/syncProducts', {});
        setSyncProductsLoading(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Product data synced";
            props.changeToastActive(true, message);
        }
    };

    const syncCustomers = async () => {
        setSyncCustomersLoading(true);
        props.setIsLoading(true);
        const data = await fetch('api/home/syncCustomers', {});
        setSyncCustomersLoading(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Customer data synced";
            props.changeToastActive(true, message);
        }
    };

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    useEffect(() => {
        document.title = "Stack Discounts";
        populateData();
        getMainThemeSettingsData();
        getUser();
        getRecentCampaigns();
        getRating();
        getQuickStats(initSettings.qsDateRange);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!loading && JSON.stringify(model) !== '{}') {
            var notificationIcon = document.querySelector('.Polaris-Page-Header__RightAlign');
            if (notificationIcon) {
                notificationIcon.style.position = 'relative';
                notificationIcon.style.padding = '0.6rem 0.8rem 0 0';

                if (model.unreadAnnouncementCount > 0) {
                    var unreadIndicator = document.createElement('span');
                    unreadIndicator.classList.add('unread-indicator');
                    unreadIndicator.innerHTML = model.unreadAnnouncementCount;
                    unreadIndicator.addEventListener("click", async (e) => {
                        if (props.user.viewOnly) return;
                        navigate('/notifications');
                    });
                    notificationIcon.appendChild(unreadIndicator);
                }
            }
        }
        // eslint-disable-next-line
    }, [loading, model]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const themePromoValentines = (
        <MediaCard
            title={<Text variant="headingXl" as="h4" fontWeight="bold">💖 Valentine's Day is Coming! 💖</Text>}
            primaryAction={{
                content: 'Check out the demo here!',
                icon: ExternalSmallIcon,
                onAction: () => { window.open("https://stack-discounts-demo.myshopify.com/?_bt=BAh7BkkiC19yYWlscwY6BkVUewhJIglkYXRhBjsAVEkiJ3N0YWNrLWRpc2NvdW50cy1kZW1vLm15c2hvcGlmeS5jb20GOwBGSSIIZXhwBjsAVEkiHTIwMjUtMTAtMjRUMDk6MTk6NTguNTc4WgY7AFRJIghwdXIGOwBUSSIecGVybWFuZW50X3Bhc3N3b3JkX2J5cGFzcwY7AEY%3D--33c468e7448a98a3c602445175ec665e6db8a3de", '_blank'); },
            }}
            onDismiss={() => {
                updateInitSetting('showPromo', false);
            }}
            description={
                <span className="home-theme-promo-content">
                    <span>Celebrate love and boost your sales by showcasing exclusive discounts with our <strong>Valentine’s Day theme and widget</strong>!</span>
                    <span className="how-to-setup">🎨 How to Set It Up:</span>
                    <span className="list-item">1. Go to the <Link url="/themes">Themes</Link> page, add and configure the Valentine’s Day special theme.</span>
                    <span className="list-item">2. In <Link url="/settings">Settings</Link>, ensure the "Hide widget" checkbox is unchecked so your customers can see the discounts.</span>
                </span>
            }
            size="small"
        >
            <img alt="" width="100%" height="100%"
                style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                }}
                src="https://stackdiscounts.deals.today/themes/valentines-day-v1/logo_large.svg"
            />
        </MediaCard>
    );

    const themePromoHalloween = (
        <MediaCard
            title={<Text variant="headingXl" as="h4" fontWeight="bold">🎃 Halloween is Coming! 🎃</Text>}
            primaryAction={{
                content: 'Check out the demo here!',
                icon: ExternalSmallIcon,
                onAction: () => { window.open("https://stack-discounts-demo.myshopify.com/?_bt=BAh7BkkiC19yYWlscwY6BkVUewhJIglkYXRhBjsAVEkiJ3N0YWNrLWRpc2NvdW50cy1kZW1vLm15c2hvcGlmeS5jb20GOwBGSSIIZXhwBjsAVEkiHTIwMjUtMTAtMjRUMDk6MTk6NTguNTc4WgY7AFRJIghwdXIGOwBUSSIecGVybWFuZW50X3Bhc3N3b3JkX2J5cGFzcwY7AEY%3D--33c468e7448a98a3c602445175ec665e6db8a3de", '_blank'); },
            }}
            onDismiss={() => {
                updateInitSetting('showPromo', false);
            }}
            description={
                <span className="home-theme-promo-content">
                    <span>Spook up your sales by showcasing exclusive discounts with our <strong>Halloween theme and widget</strong>!</span>
                    <span className="how-to-setup">🎨 How to Set It Up:</span>
                    <span className="list-item">1. Go to the <Link url="/themes">Themes</Link> page, add and configure the Halloween special theme.</span>
                    <span className="list-item">2. In <Link url="/settings">Settings</Link>, ensure the "Hide widget" checkbox is unchecked so your customers can see the discounts.</span>
                </span>
            }
            size="small"
        >
            <img alt="" width="100%" height="100%"
                style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                }}
                src="https://stackdiscounts.deals.today/themes/halloween-v1/logo_large.svg"
            />
        </MediaCard>
    );

    const modalFeedback = (
        <Modal
            title={'Send your feedback'}
            open={modalFeedbackActive}
            onClose={() => {
                setModalFeedbackActive(false);
            }}
            primaryAction={{
                content: 'Send',
                onAction: async () => {
                    var hasError = false;
                    if (!rating?.feedback) {
                        handleError('feedback', true);
                        hasError = true;
                    }
                    if (rating?.email && !isValidEmail(rating?.email)) {
                        handleError('email', true);
                        hasError = true;
                    }
                    if (hasError) {
                        return;
                    }

                    setModalFeedbackActive(false);
                    props.changeToastActive(true, 'Feedback sent');

                    updateInitSetting('showRating', false);

                    await fetch('api/home/saveRating', rating);

                    const updated = {
                        ...rating,
                        feedback: '',
                        email: ''
                    };
                    setRating(updated);
                },
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => {
                        setModalFeedbackActive(false);
                    },
                },
            ]}
        >
            <div id="modal-feedback">
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            value={rating?.feedback ?? ''}
                            onChange={(newValue) => {
                                const updated = {
                                    ...rating,
                                    feedback: newValue
                                };
                                setRating(updated);
                            }}
                            onBlur={() => handleError('feedback', !rating?.feedback)}
                            label="Your feedback helps us improve the app."
                            multiline={2}
                            placeholder="What could we improve?"
                            error={error.feedback ? 'Feedback is required' : ''}
                        />
                        <TextField
                            value={rating?.email ?? ''}
                            onChange={(newValue) => {
                                const updated = {
                                    ...rating,
                                    email: newValue
                                };
                                setRating(updated);
                            }}
                            onBlur={() => handleError('email', !(!rating?.email || isValidEmail(rating?.email)))}
                            label="Email (optional)"
                            helpText="If provided, we'll use this email instead of your shop's primary email."
                            error={error.email ? 'Enter a valid email address' : ''}
                        />
                    </FormLayout>
                </Modal.Section>
            </div>
        </Modal>
    );

    return (
        <div className={'home-page'}>
            <Page title={'Hello ' + (props.user.name ? props.user.name : '') + ' 👋'}
                secondaryActions={[
                    {
                        content: 'Notifications', icon: NotificationIcon, onAction: () => {
                            if (loading || props.user.viewOnly) return;
                            navigate('/notifications');
                        }
                    }
                ]}
            >
                {modalFeedback}
                <Layout>
                    {
                        initSettings.showRating ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <InlineGrid columns="1fr auto" gap="100" alignItems="center">
                                            <InlineStack gap="300" blockAlign="center">
                                                <InlineStack gap="200">
                                                    <img src={starIconCustom} alt="Number of campaigns" className="rate-main-icon" width={36} height={36} />
                                                    <Text fontWeight="semibold">{rating?.title ?? 'Help us make this better for you.'}</Text>
                                                    <Text>{rating?.subtitle ?? 'Leave a quick rating - it really helps.'}</Text>
                                                </InlineStack>
                                                {
                                                    rating?.askFeedback ?
                                                        <InlineStack gap="100">
                                                            <Button onClick={() => setModalFeedbackActive(true)}>Share feedback</Button>
                                                        </InlineStack>
                                                        :
                                                        <div className="rating-stars">
                                                            <span className={"rating-star" + (rating?.rate > 4 ? ' rated' : '')} onClick={() => saveRating(5)}></span>
                                                            <span className={"rating-star" + (rating?.rate > 3 ? ' rated' : '')} onClick={() => saveRating(4)}></span>
                                                            <span className={"rating-star" + (rating?.rate > 2 ? ' rated' : '')} onClick={() => saveRating(3)}></span>
                                                            <span className={"rating-star" + (rating?.rate > 1 ? ' rated' : '')} onClick={() => saveRating(2)}></span>
                                                            <span className={"rating-star" + (rating?.rate > 0 ? ' rated' : '')} onClick={() => saveRating(1)}></span>
                                                        </div>
                                                }
                                            </InlineStack>
                                            <div className="icon-20">
                                                <Button variant="tertiary" icon={XSmallIcon} onClick={() => closeRating()} />
                                            </div>
                                        </InlineGrid>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    {
                        !loading && model.isFreePlan ?
                            <Layout.Section>
                                <Banner
                                    title="Free plan limitations"
                                    status="warning"
                                >
                                    <p>You can have a maximum of 2 <Link url="/campaigns">campaigns</Link> with 2 discounts assigned.</p>
                                    <p>You can always upgrade to a paid plan on the <Link url="/settings/billing">Billing</Link> page and avoid all limitations.</p>
                                </Banner>
                            </Layout.Section>
                            : null
                    }
                    {
                        initSettings.showPromo && false ?
                            <Layout.Section>
                                <div className="home-theme-promo">
                                    {themePromoHalloween}
                                </div>
                            </Layout.Section>
                            : null
                    }
                    {
                        !initSettings.appEnabled || !model.enabled ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <div className="qs-title">
                                                <Text as="h2" variant="headingMd">
                                                    Enabling required!
                                                </Text>
                                            </div>
                                            <Banner tone={enabledLoading ? 'warning' : 'critical'}>
                                                <InlineStack align="space-between" blockAlign="center" gap="200">
                                                    <BlockStack>
                                                        <Text as="p">
                                                            {
                                                                enabledLoading ?
                                                                    'Recheck state button will update the current state of the app.' :
                                                                    'This step is required only for syncing with your storefront.'
                                                            }
                                                        </Text>
                                                        {
                                                            !enabledLoading ?
                                                                <Text as="p">
                                                                    It will not affect your theme's visuals. <a href="https://uniocode.gitbook.io/stack-discounts/1.-getting-started" rel="noreferrer" target="_blank" className="Polaris-Link Polaris-Link--monochrome ml-5">Lear more</a>
                                                                </Text>
                                                                : null
                                                        }
                                                    </BlockStack>
                                                    {
                                                        enabledLoading ?
                                                            <Button
                                                                onClick={() => {
                                                                    if (props.user.viewOnly) return;

                                                                    setStateLoading(true);
                                                                    getMainThemeSettingsData();
                                                                }}
                                                                loading={stateLoading}
                                                                variant="secondary"
                                                            >Recheck state</Button>
                                                            :
                                                            <Button
                                                                onClick={() => {
                                                                    if (props.user.viewOnly) return;

                                                                    setEnabledLoading(true);
                                                                    window.open(model.extensionDeepLink, '_blank');
                                                                }}
                                                                icon={ExternalSmallIcon}
                                                                variant="primary"
                                                            >Enable app</Button>
                                                    }
                                                </InlineStack>
                                            </Banner>
                                        </BlockStack>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    {
                        !model.liveMode ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <div className="qs-title">
                                                <Text as="h2" variant="headingMd">
                                                    Preview mode is being deprecated!
                                                </Text>
                                            </div>
                                            <Banner tone="warning">
                                                <InlineStack align="space-between" blockAlign="center" gap="200">
                                                    <BlockStack gap="200">
                                                        <BlockStack>
                                                            <Text as="p">
                                                                Your app is currently not live, so discounts will not be visible to customers.
                                                            </Text>
                                                            <Text as="p">
                                                                After going live, you won’t be able to switch back to Preview mode.
                                                            </Text>
                                                        </BlockStack>
                                                        <BlockStack>
                                                            <Text as="p">
                                                                You can still test campaigns before making them public.
                                                            </Text>
                                                            <Text as="p">
                                                                Use Customer eligibility → Specific customers and select only your test customer account.
                                                            </Text>
                                                        </BlockStack>
                                                    </BlockStack>
                                                    <Button
                                                        onClick={() => {
                                                            if (props.user.viewOnly) return;
                                                            changeLiveMode(true);
                                                        }}
                                                        loading={liveModeLoading}
                                                        variant="secondary"
                                                    >Go Live</Button>
                                                </InlineStack>
                                            </Banner>
                                        </BlockStack>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    <Layout.Section>
                        <div className="card-main">
                            <Card>
                                <BlockStack gap="300">
                                    <InlineGrid columns={{ xs: '1fr', md: '1fr auto' }} gap="200">
                                        <InlineStack gap="100" blockAlign="center">
                                            <div className="qs-title">
                                                <Text as="h2" variant="headingMd">
                                                    Quick Stats
                                                </Text>
                                            </div>
                                            <div className={'app-state ' + (enabledLoading ? 'recheck' : (initSettings.appEnabled && model.enabled ? 'enabled' : 'disabled'))}>
                                                {
                                                    stateLoading ?
                                                        <Spinner size="small" />
                                                        :
                                                        <Icon
                                                            source={enabledLoading ? RefreshIcon : (initSettings.appEnabled && model.enabled ? CheckCircleIcon : XCircleIcon)}
                                                            tone="base"
                                                        />
                                                }
                                                {enabledLoading ? '' : (initSettings.appEnabled && model.enabled ? 'Enabled in' : 'Disabled in')}
                                                <a href={model.extensionDeepLink} onClick={(e) => {
                                                    e.preventDefault();
                                                    if (props.user.viewOnly) return;

                                                    if (enabledLoading) {
                                                        setStateLoading(true);
                                                        getMainThemeSettingsData();
                                                    }
                                                    else {
                                                        setEnabledLoading(true);
                                                        window.open(model.extensionDeepLink, '_blank');
                                                    }
                                                }}>{enabledLoading ? 'Recheck app state' : 'Theme settings'}</a>
                                            </div>
                                        </InlineStack>
                                        <InlineStack align="space-between" wrap gap="100" blockAlign="center">
                                            <InlineStack gap="100" wrap blockAlign="center">
                                                <div className={initSettings.qsDateRange === 'last24Hours' ? 'filter-active' : ''}>
                                                    <Button variant="tertiary" onClick={(e) => { getQuickStats('last24Hours'); }}>24 hours</Button>
                                                </div>
                                                <div className={initSettings.qsDateRange === 'last7Days' ? 'filter-active' : ''}>
                                                    <Button variant="tertiary" onClick={(e) => { getQuickStats('last7Days'); }}>7 days</Button>
                                                </div>
                                                <div className={initSettings.qsDateRange === 'last30Days' ? 'filter-active' : ''}>
                                                    <Button variant="tertiary" onClick={(e) => { getQuickStats('last30Days'); }}>30 days</Button>
                                                </div>
                                                <div className={!initSettings.qsDateRange || initSettings.qsDateRange === 'allTime' ? 'filter-active' : ''}>
                                                    <Button variant="tertiary" onClick={(e) => { getQuickStats('allTime'); }}>All time</Button>
                                                </div>
                                            </InlineStack>
                                            <Button icon={ChartVerticalFilledIcon} onClick={() => {
                                                navigate('/analytics', { state: { from: 'home' } });
                                            }}>
                                                View analytics
                                            </Button>
                                        </InlineStack>
                                    </InlineGrid>
                                    <InlineGrid gap="200" columns={{ xs: 2, md: 2, lg: 4 }}>
                                        <div className="qs-box">
                                            <div className="qs-box-title">
                                                <img src={qsDiscountIconCustom} alt="Number of campaigns" />
                                                Number of campaigns
                                            </div>
                                            <div className="qs-box-values">
                                                <div className="qs-box-value">
                                                    <span>Total</span>
                                                    {quickStats ? <span>{quickStats.totalCampaigns}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                                <div className="qs-box-value">
                                                    <span>Active</span>
                                                    {quickStats ? <span>{quickStats.activeCampaigns}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="qs-box">
                                            <div className="qs-box-title">
                                                <img src={qsBarIconCustom} alt="Impressions" />
                                                Impressions
                                            </div>
                                            <div className="qs-box-values">
                                                <div className="qs-box-value">
                                                    <span>Impressions</span>
                                                    {quickStats ? <span>{quickStats.impressions}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                                <div className="qs-box-value">
                                                    <span>Applies</span>
                                                    {quickStats ? <span>{quickStats.applies}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="qs-box">
                                            <div className="qs-box-title">
                                                <img src={qsRefreshIconCustom} alt="Conversion" />
                                                Conversion
                                            </div>
                                            <div className="qs-box-values">
                                                <div className="qs-box-value">
                                                    <span>Converted</span>
                                                    {quickStats ? <span>{quickStats.converted}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="qs-box">
                                            <div className="qs-box-title">
                                                <img src={qsDollarIconCustom} alt="Provided discount" />
                                                Provided discount
                                            </div>
                                            <div className="qs-box-values">
                                                <div className="qs-box-value">
                                                    <span>Total amount</span>
                                                    {quickStats ? <span>{formatMoney(quickStats.discountAmount, props.user.moneyFormat)}</span> : <SkeletonDisplayText size="small" />}
                                                </div>
                                            </div>
                                        </div>
                                    </InlineGrid>
                                </BlockStack>
                            </Card>
                        </div>
                    </Layout.Section>
                    {
                        initSettings.numberOfCampaigns > 0 ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <InlineGrid columns="1fr auto">
                                                <InlineStack blockAlign="center" gap="200">
                                                    <Text as="h2" variant="headingMd">
                                                        Campaigns
                                                    </Text>
                                                    <Badge tone="info">{recentCampaigns ? recentCampaigns.totalCount : '-'}</Badge>
                                                </InlineStack>
                                                <InlineStack blockAlign="center" gap="200">
                                                    <Button variant="primary" icon={PlusIcon}
                                                        onClick={() => {
                                                            navigate('/campaigns/new', { state: { from: 'home' } });
                                                        }}
                                                    >
                                                        Create new
                                                    </Button>
                                                    <Button
                                                        onClick={() => {
                                                            navigate('/campaigns', { state: { from: 'home' } });
                                                        }}
                                                    >
                                                        View all
                                                    </Button>
                                                </InlineStack>
                                            </InlineGrid>
                                            <div className="recent-campaigns">
                                                {
                                                    recentCampaigns ?
                                                        <IndexTable
                                                            resourceName={{ singular: 'campaign', plural: 'campaigns' }}
                                                            itemCount={recentCampaigns.items.length}
                                                            selectable={false}
                                                            headings={[
                                                                { title: 'Name' },
                                                                { title: 'Method' },
                                                                { title: 'Discounts', alignment: 'center' },
                                                                { title: 'Status', alignment: 'end' },
                                                            ]}
                                                        >
                                                            {
                                                                recentCampaigns.items.map((item, index) => (
                                                                    <IndexTable.Row
                                                                        id={item.id}
                                                                        key={item.id}
                                                                        position={index}
                                                                    >
                                                                        <IndexTable.Cell>
                                                                            <Link
                                                                                dataPrimaryLink
                                                                                url={`/campaigns/${item.id}`}
                                                                                removeUnderline
                                                                                monochrome
                                                                                onClick={(e) => {
                                                                                    e.preventDefault(); // stop default navigation
                                                                                    navigate(`/campaigns/${item.id}`, { state: { from: 'home' } });
                                                                                }}
                                                                            >
                                                                                <Text variant="bodyMd" fontWeight="semibold" as="span">
                                                                                    {item.displayName}
                                                                                </Text>
                                                                            </Link>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <InlineStack gap="100" wrap={false} blockAlign="center">
                                                                                <Text as="span">
                                                                                    {item.method + (item.campaignMethodId === 2 || item.campaignMethodId === 3 ? ':' : '')}
                                                                                </Text>
                                                                                {
                                                                                    item.campaignMethodId === 2 ?
                                                                                        <InlineStack gap="200" blockAlign="center">
                                                                                            <Text as="span" fontWeight="medium">{item.code}</Text>
                                                                                            <div className="resource-item-copy-code display-flex-span">
                                                                                                <Tooltip content="Copy code">
                                                                                                    <Button disabled={!item.code} variant="tertiary" icon={ClipboardIcon} onClick={(e) => copyToClipboard(item.code ?? '', e)} />
                                                                                                </Tooltip>
                                                                                            </div>
                                                                                        </InlineStack>
                                                                                        : null
                                                                                }
                                                                                {
                                                                                    item.campaignMethodId === 3 ?
                                                                                        <Text as="span">{item.codeCount}</Text>
                                                                                        : null
                                                                                }
                                                                            </InlineStack>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <Text as="span" alignment="center" numeric>
                                                                                {item.discountsCount}
                                                                            </Text>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <Text alignment="end">
                                                                                <Badge tone={item.statusName === "Active" ? "success" : (item.statusName === "Scheduled" ? "attention" : "default")}>{item.statusName}</Badge>
                                                                            </Text>
                                                                        </IndexTable.Cell>
                                                                    </IndexTable.Row>
                                                                ))
                                                            }
                                                            <IndexTable.Row id="footer" key="footer" position={recentCampaigns.items.length}>
                                                                <IndexTable.Cell colSpan="4">
                                                                    <span className="index-view-all">Showing latest {recentCampaigns.items.length} of {recentCampaigns.totalCount}</span>
                                                                </IndexTable.Cell>
                                                            </IndexTable.Row>
                                                        </IndexTable>
                                                        :
                                                        <IndexTable
                                                            resourceName={{ singular: 'campaign', plural: 'campaigns' }}
                                                            itemCount={initSettings.numberOfCampaigns}
                                                            selectable={false}
                                                            headings={[
                                                                { title: 'Name' },
                                                                { title: 'Method' },
                                                                { title: 'Discounts', alignment: 'center' },
                                                                { title: 'Status', alignment: 'end' },
                                                            ]}
                                                        >
                                                            {
                                                                [...Array(initSettings.numberOfCampaigns).keys()].map((item, index) => (
                                                                    <IndexTable.Row
                                                                        id={item}
                                                                        key={item}
                                                                        position={index}
                                                                    >
                                                                        <IndexTable.Cell>
                                                                            <InlineStack align="start">
                                                                                <Box width="120px">
                                                                                    <SkeletonDisplayText size="small" />
                                                                                </Box>
                                                                            </InlineStack>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <InlineStack align="start">
                                                                                <Box width="120px">
                                                                                    <SkeletonDisplayText size="small" />
                                                                                </Box>
                                                                            </InlineStack>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <InlineStack align="center">
                                                                                <Box width="20px">
                                                                                    <SkeletonDisplayText size="small" />
                                                                                </Box>
                                                                            </InlineStack>
                                                                        </IndexTable.Cell>
                                                                        <IndexTable.Cell>
                                                                            <InlineStack align="end">
                                                                                <Box width="75px">
                                                                                    <SkeletonDisplayText size="small" />
                                                                                </Box>
                                                                            </InlineStack>
                                                                        </IndexTable.Cell>
                                                                    </IndexTable.Row>
                                                                ))
                                                            }
                                                            <IndexTable.Row id="footer" key="footer" position={3}>
                                                                <IndexTable.Cell colSpan="4">
                                                                    <span className="index-view-all">Showing latest 3 of N</span>
                                                                </IndexTable.Cell>
                                                            </IndexTable.Row>
                                                        </IndexTable>
                                                }
                                            </div>
                                        </BlockStack>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    <Layout.Section>
                        <div className="card-main">
                            <Card>
                                <div className="p-10">
                                    <BlockStack gap="200">
                                        {
                                            initSettings.numberOfCampaigns === 0 || (recentCampaigns && recentCampaigns.totalCount === 0) ?
                                                <div className="no-campaign">
                                                    <div className="no-campaign-icon">
                                                        <Icon source={SearchIcon} />
                                                    </div>
                                                    <div className="no-campaign-title">
                                                        No campaigns yet
                                                    </div>
                                                    <div className="no-campaign-text">
                                                        A campaign is a group of discounts that work together under a single setup. It defines when, how, and to whom those discounts are applied.
                                                    </div>
                                                </div>
                                                : null
                                        }
                                        <InlineGrid gap={{ xs: "1000", md: "400", lg: "1000" }} columns={{ xs: 1, md: 3 }}>
                                            <div className="create-campaign">
                                                <div className="create-campaign-title">
                                                    <img src={automaticIconCustom} alt="automatic" width={44} height={44} />
                                                    Automatic
                                                </div>
                                                <div className="create-campaign-text">Combine multiple discounts into one campaign that applies automatically.</div>
                                                <div className="create-campaign-button"><Button size="large" fullWidth onClick={() => {
                                                    navigate('/campaigns/new', { state: { from: 'home' } });
                                                }}>Create Automatic</Button></div>
                                            </div>
                                            <div className="create-campaign">
                                                <div className="create-campaign-title">
                                                    <img src={codeIconCustom} alt="code" width={44} height={44} />
                                                    Code
                                                </div>
                                                <div className="create-campaign-text">A single code can apply multiple discounts within the same campaign.</div>
                                                <div className="create-campaign-button"><Button size="large" fullWidth onClick={() => {
                                                    navigate('/campaigns/new/method/2', { state: { from: 'home' } });
                                                }}>Create Code</Button></div>
                                            </div>
                                            <div className="create-campaign">
                                                <div className="create-campaign-title">
                                                    <img src={bulkIconCustom} alt="bulk" width={44} height={44} />
                                                    Bulk
                                                </div>
                                                <div className="create-campaign-text">Generate multiple unique codes, all powered by the same campaign logic.</div>
                                                <div className="create-campaign-button"><Button size="large" fullWidth onClick={() => {
                                                    navigate('/campaigns/new/method/3', { state: { from: 'home' } });
                                                }}>Create Bulk</Button></div>
                                            </div>
                                        </InlineGrid>
                                    </BlockStack>
                                </div>
                            </Card>
                        </div>
                    </Layout.Section>
                    <Layout.Section>
                        {
                            loading ?
                                <LegacyCard sectioned>
                                    <SkeletonBodyText lines={10} />
                                </LegacyCard>
                                :
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <InlineGrid columns="1fr auto">
                                                <InlineStack blockAlign="center" gap="200">
                                                    <Text as="h2" variant="headingMd">
                                                        Widget
                                                    </Text>
                                                </InlineStack>
                                            </InlineGrid>
                                            <div className="widget-box">
                                                <InlineGrid gap="400" columns={{ xs: 1, md: 2 }}>
                                                    <Card roundedAbove="sm">
                                                        <BlockStack gap="400">
                                                            <BlockStack gap="200">
                                                                <Text as="h3" variant="headingSm">
                                                                    Display active discounts seamlessly with the widget
                                                                </Text>
                                                                <Text as="p" variant="bodyMd">
                                                                    To show the widget, go to Settings and uncheck the Hide widget box.
                                                                    You can also customize the widget's animation, placement, text, and more.
                                                                </Text>
                                                            </BlockStack>
                                                            <InlineStack align="center">
                                                                <Button variant="tertiary" fullWidth onClick={() => {
                                                                    navigate('/settings');
                                                                }}
                                                                >{'Settings'}
                                                                </Button>
                                                            </InlineStack>
                                                        </BlockStack>
                                                    </Card>
                                                    <Card roundedAbove="sm">
                                                        <BlockStack gap="400">
                                                            <BlockStack gap="200">
                                                                <Text as="h3" variant="headingSm">
                                                                    Personalize widget's colors and title
                                                                </Text>
                                                                <Text as="p" variant="bodyMd">
                                                                    Go to Themes and click Customize on the main theme.<br />
                                                                    Schedule or apply ready-made themes for holidays and special occasions.
                                                                </Text>
                                                            </BlockStack>
                                                            <InlineStack align="center">
                                                                <Button variant="tertiary" fullWidth onClick={() => {
                                                                    navigate('/themes');
                                                                }}
                                                                >{'Themes'}
                                                                </Button>
                                                            </InlineStack>
                                                        </BlockStack>
                                                    </Card>
                                                </InlineGrid>
                                            </div>
                                        </BlockStack>
                                    </Card>
                                </div>
                        }
                    </Layout.Section>
                    {
                        ((props.user.adminEnabled && !props.user.viewOnly) || props.user.myShopifyDomain === 'potluck-dev.myshopify.com' || props.user.myShopifyDomain === 'babycubby.myshopify.com' || props.user.myShopifyDomain === 'dnavibe.myshopify.com' || props.user.myShopifyDomain === 'creative-1978.myshopify.com' || props.user.myShopifyDomain === 'fe6bb9-09.myshopify.com') ?
                            <Layout.Section>
                                {
                                    loading ?
                                        <LegacyCard sectioned>
                                            <SkeletonBodyText lines={10} />
                                        </LegacyCard>
                                        :
                                        <div className="card-main">
                                            <Card>
                                                <BlockStack gap="300">
                                                    <InlineGrid columns="1fr auto">
                                                        <InlineStack blockAlign="center" gap="200">
                                                            <Text as="h2" variant="headingMd">
                                                                Heads Up
                                                            </Text>
                                                        </InlineStack>
                                                    </InlineGrid>
                                                    <div className="widget-box">
                                                        <InlineGrid gap="400" columns={{ xs: 1, md: 1 }}>
                                                            {
                                                                //props.user.adminEnabled || props.user.myShopifyDomain === 'b94363.myshopify.com' || props.user.myShopifyDomain === 'discounts-07.myshopify.com' ?
                                                                props.user.adminEnabled || props.user.myShopifyDomain === 'potluck-dev.myshopify.com' || props.user.myShopifyDomain === 'babycubby.myshopify.com' || props.user.myShopifyDomain === 'dnavibe.myshopify.com' || props.user.myShopifyDomain === 'creative-1978.myshopify.com' ?
                                                                    <Card roundedAbove="sm">
                                                                        <InlineStack blockAlign="center" gap="300" align="space-between">
                                                                            <BlockStack gap="200">
                                                                                <Text as="h3" variant="headingSm">
                                                                                    Manual synchronization may be required
                                                                                </Text>
                                                                                <Text as="p" variant="bodyMd">
                                                                                    The app automatically synchronizes certain product data with your store.<br />
                                                                                    In rare cases (such as recent collection updates with POS or Headless Commerce) a manual sync may be required.
                                                                                </Text>
                                                                            </BlockStack>
                                                                            <Button onClick={() => {
                                                                                if (props.user.viewOnly) return;
                                                                                syncProducts();
                                                                            }}
                                                                                loading={syncProductsLoading}
                                                                            >{'Sync'}
                                                                            </Button>
                                                                        </InlineStack>
                                                                    </Card>
                                                                    : null
                                                            }
                                                            {
                                                                props.user.adminEnabled || props.user.myShopifyDomain === 'fe6bb9-09.myshopify.com' ?
                                                                    <Card roundedAbove="sm">
                                                                        <InlineStack blockAlign="center" gap="300" align="space-between">
                                                                            <BlockStack gap="200">
                                                                                <Text as="h3" variant="headingSm">
                                                                                    Manual synchronization for Customer segments
                                                                                </Text>
                                                                                <Text as="p" variant="bodyMd">
                                                                                    Manual synchronization for Customer segments
                                                                                </Text>
                                                                            </BlockStack>
                                                                            <Button onClick={() => {
                                                                                if (props.user.viewOnly) return;
                                                                                syncCustomers();
                                                                            }}
                                                                                loading={syncCustomersLoading}
                                                                            >{'Sync'}
                                                                            </Button>
                                                                        </InlineStack>
                                                                    </Card>
                                                                    : null
                                                            }
                                                        </InlineGrid>
                                                    </div>
                                                </BlockStack>
                                            </Card>
                                        </div>
                                }
                            </Layout.Section>
                            : null
                    }
                    {
                        props.user.adminEnabled && !props.user.viewOnly ?
                            <Layout.AnnotatedSection title="App Mode">
                                {
                                    loading ?
                                        <LegacyCard sectioned>
                                            <SkeletonBodyText lines={6} />
                                        </LegacyCard>
                                        :
                                        <div className="setting-toggle-card">
                                            <LegacyCard sectioned>
                                                <LegacyStack alignment="center">
                                                    <LegacyStack.Item fill>
                                                        <div>{!model.enabled ? 'Preview is disabled' : (model.liveMode ? 'View on your store' : 'Preview on your store')}</div>
                                                        <div className="custom-help-text">
                                                            {
                                                                !model.enabled ?
                                                                    'Please, enable the app first.'
                                                                    :
                                                                    (model.liveMode ? 'See the app in action in live mode.' : 'See the app in action before going live.')
                                                            }
                                                        </div>
                                                    </LegacyStack.Item>
                                                    <Button onClick={() => {
                                                        window.open(props.user.storefrontUrl.replace('?isadmin=true', '')
                                                            + (model.liveMode ? '' : '?pvt=' + model.previewToken), '_blank');
                                                    }}
                                                        disabled={!model.enabled}
                                                        icon={ExternalSmallIcon}>{model.liveMode ? 'Open' : 'Preview'}
                                                    </Button>
                                                </LegacyStack>
                                            </LegacyCard>
                                            <LegacyCard sectioned>
                                                <LegacyStack alignment="center">
                                                    <LegacyStack.Item fill>
                                                        <div>
                                                            <div className={'indicator' + (model.liveMode && model.enabled ? ' enabled' : '')}></div>
                                                            {
                                                                !model.enabled ?
                                                                    <span>
                                                                        <Text as="span" fontWeight="semibold">{model.liveMode ? 'Live mode' : 'Preview mode'}</Text>
                                                                        <span> is disabled</span>
                                                                    </span>
                                                                    :
                                                                    <span>
                                                                        <span>The app is in </span>
                                                                        <Text as="span" fontWeight="semibold">{model.liveMode ? 'Live mode' : 'Preview mode'}</Text>
                                                                    </span>
                                                            }
                                                        </div>
                                                        <div className="custom-help-text">
                                                            {
                                                                !model.enabled ?
                                                                    'Please, enable the app first.'
                                                                    :
                                                                    (model.liveMode ? 'The app is visible to all visitors. You can switch it to preview mode anytime.' : 'The app is not available to the public. Only you can preview it.')
                                                            }
                                                        </div>
                                                        {
                                                            !model.freePlanIsValid ?
                                                                <div className="custom-error-text">
                                                                    <Text as="span" tone="critical">Free plan limitations are not met. You need to remove some discounts or campaigns or upgrade to paid plan on the <Link monochrome url="/settings/billing">Billing</Link> page.</Text>
                                                                </div>
                                                                : null
                                                        }
                                                        {
                                                            !model.freePlanIsValid ?
                                                                <div className="custom-error-text">
                                                                    <Text as="span" tone="critical">Only after that, you can go live. See about limitations at the top of this page.</Text>
                                                                </div>
                                                                : null
                                                        }
                                                    </LegacyStack.Item>
                                                    <Button onClick={() => {
                                                        if (props.user.viewOnly) return;
                                                        changeLiveMode(!model.liveMode)
                                                    }}
                                                        disabled={!model.enabled || !model.freePlanIsValid}
                                                        loading={liveModeLoading}
                                                    >{model.liveMode ? 'Disable' : 'Go Live'}
                                                    </Button>
                                                </LegacyStack>
                                            </LegacyCard>
                                        </div>
                                }
                            </Layout.AnnotatedSection>
                            : null
                    }
                </Layout>
            </Page>
        </div>
    );
}